import { useEffect, useState, useRef } from 'react';
import * as THREE from 'three';
import useEditorStore from '../../stores/useEditorStore';
import { useShallow } from 'zustand/shallow';

/**
 * A component that manages the cursor style for a vanilla three.js scene.
 * It handles cursors for rotating, panning, zooming, hovering, and external overrides.
 */
export const CursorManager = () => {
  // === Internal State ===
  const [isDragging, setIsDragging] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [activeMouseButton, setActiveMouseButton] = useState(null);
  // New: State to track zoom direction ('in', 'out', or null)
  const [zoomDirection, setZoomDirection] = useState(null);

  // === Zustand Store Integration ===
  const { sceneRefs, interactiveObjects, cursorOverride, paintingCursor } = useEditorStore(
    useShallow((state) => ({
      sceneRefs: state.sceneRefs,
      interactiveObjects: state.interactiveObjects,
      cursorOverride: state.cursorOverride,
      paintingCursor: state.paintingCursor,
    }))
  );

  // === Refs for performance and timeouts ===
  const raycaster = useRef(new THREE.Raycaster());
  const mouse = useRef(new THREE.Vector2());
  // New: Ref to manage the timeout for resetting the zoom cursor
  const zoomTimeoutRef = useRef(null);

  // === Event Listener Effects ===

  // Effect to handle OrbitControls drag, mouse buttons, and zoom events.
  useEffect(() => {
    const controls = sceneRefs?.controls;
    const canvas = sceneRefs?.renderer?.domElement;
    if (!controls || !canvas) return;

    // --- OrbitControls Listeners ---
    const onStart = () => setIsDragging(true);
    const onEnd = () => setIsDragging(false);
    controls.addEventListener('start', onStart);
    controls.addEventListener('end', onEnd);

    // --- Mouse Button Listeners ---
    const handleMouseDown = (e) => setActiveMouseButton(e.button);
    const handleMouseUp = () => setActiveMouseButton(null);
    canvas.addEventListener('pointerdown', handleMouseDown);
    window.addEventListener('pointerup', handleMouseUp);

    // --- New: Zoom (Wheel) Listener ---
    const handleWheel = (event) => {
      // Clear any existing timeout to ensure the latest scroll event is handled
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current);
      }

      // Determine zoom direction based on scroll delta
      setZoomDirection(event.deltaY < 0 ? 'in' : 'out');

      // Set a timeout to reset the zoom cursor after a short period
      zoomTimeoutRef.current = setTimeout(() => {
        setZoomDirection(null);
      }, 300); // Show zoom cursor for 300ms
    };
    canvas.addEventListener('wheel', handleWheel);


    return () => {
      // Cleanup all listeners
      controls.removeEventListener('start', onStart);
      controls.removeEventListener('end', onEnd);
      canvas.removeEventListener('pointerdown', handleMouseDown);
      window.removeEventListener('pointerup', handleMouseUp);
      canvas.removeEventListener('wheel', handleWheel);
      // Clear timeout on cleanup
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current);
      }
    };
  }, [sceneRefs?.controls, sceneRefs?.renderer]);

  // Effect to handle hover detection.
  useEffect(() => {
    const canvas = sceneRefs?.renderer?.domElement;
    const camera = sceneRefs?.camera;
    if (!canvas || !camera) return;

    const handleMouseMove = (event) => {
      if (isDragging || cursorOverride || zoomDirection) {
        setIsHovering(false);
        return;
      }
      const rect = canvas.getBoundingClientRect();
      mouse.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.current.setFromCamera(mouse.current, camera);
      const intersects = raycaster.current.intersectObjects(interactiveObjects || []);
      setIsHovering(intersects.length > 0);
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    return () => canvas.removeEventListener('mousemove', handleMouseMove);
  }, [sceneRefs?.renderer, sceneRefs?.camera, interactiveObjects, isDragging, cursorOverride, zoomDirection]);

  // === Main Cursor Logic Effect ===

  // Effect to apply the final cursor style based on priority.
  useEffect(() => {
    let cursorStyle = 'auto';

    // Priority 1: External override (e.g., 'none' from a tool)
    if (cursorOverride) {
      cursorStyle = cursorOverride;
    }
    // Priority 2: User is actively zooming
    else if (zoomDirection === 'in') {
      cursorStyle = 'zoom-in';
    } else if (zoomDirection === 'out') {
      cursorStyle = 'zoom-out';
    }
    // Priority 3: User is dragging with OrbitControls
    else if (isDragging) {
      switch (activeMouseButton) {
        case 0: // Left mouse button for rotation
          cursorStyle = 'grabbing';
          break;
        case 2: // Right mouse button for panning
          cursorStyle = 'move';
          break;
        default:
          cursorStyle = 'grabbing';
      }
    }
    // Priority 4: User is hovering over an interactive object
    // else if (isHovering) {
    //   cursorStyle = 'pointer';
    // }
    
    // Apply the calculated style to the canvas element.
    if (sceneRefs?.renderer) {
        const canvas = sceneRefs.renderer.domElement;
        canvas.style.cursor = cursorStyle;

        // If painting cursor is enabled, draw a circle cursor using CSS overlay
        if (paintingCursor?.enabled) {
          const size = Math.max(2, Math.round((paintingCursor.radiusPx || 10) * 2));
          const bg = '#ffffff00';
          // Add transparency to the color (default to white with 0.4 alpha)
          const strokeColor = paintingCursor.color
            ? paintingCursor.color.replace(/^#([0-9a-f]{6})$/i, '#$1' + 'ff') // add alpha if not present
            : 'rgba(255,255,255,0.4)';
          const strokeWidth = 4;
          const svg = encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}'><circle cx='${size/2}' cy='${size/2}' r='${(size-strokeWidth)/2}' fill='${bg}' stroke='${strokeColor}' stroke-width='${strokeWidth}'/></svg>`);
          canvas.style.cursor = `url("data:image/svg+xml,${svg}") ${Math.round(size/2)} ${Math.round(size/2)}, crosshair`;
        }
    }

    // Cleanup function to reset cursor when component unmounts.
    return () => {
      if (sceneRefs?.renderer) {
        sceneRefs.renderer.domElement.style.cursor = 'auto';
      }
    };
  }, [isDragging, isHovering, cursorOverride, activeMouseButton, zoomDirection, sceneRefs?.renderer, paintingCursor?.enabled, paintingCursor?.radiusPx, paintingCursor?.color]);


  return null;
};
